'use client';

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import { Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

export function ThemeToggle() {
  const [isDark, setIsDark] = useState(false);
  const { trackEvent } = useInteractionTracker();

  useEffect(() => {
    // Check initial theme - default to dark theme
    const theme = localStorage.getItem('theme');
    const shouldBeDark = theme !== 'light';

    setIsDark(shouldBeDark);
    // Prevent transition delays on initial theme load
    document.documentElement.classList.add('no-transitions');
    document.documentElement.classList.toggle('dark', shouldBeDark);
    document.documentElement.classList.toggle('light', !shouldBeDark);
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('no-transitions');
    });
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    const previousTheme = isDark ? 'dark' : 'light';
    const nextTheme = newIsDark ? 'dark' : 'light';

    setIsDark(newIsDark);
    localStorage.setItem('theme', nextTheme);

    // Temporarily disable transitions to prevent delay during theme change
    document.documentElement.classList.add('no-transitions');
    document.documentElement.classList.toggle('dark', newIsDark);
    document.documentElement.classList.toggle('light', !newIsDark);

    // Re-enable transitions after theme change is complete
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('no-transitions');
    });

    // Track interaction (nav section)
    void trackEvent({
      action: 'click-theme-toggle',
      category: 'nav',
      label: `theme-switch-${previousTheme}-to-${nextTheme}`,
    });
  };

  return (
    <button
      aria-label="Toggle theme"
      aria-pressed={isDark}
      className="p-3 rounded-lg transition-colors cursor-pointer focus-ring-primary
        text-secondary hover:text-primary hover:bg-surface-hover"
      onClick={toggleTheme}
      type="button"
    >
      <Sun aria-hidden="true" className="block dark:hidden" size={20} />
      <Moon aria-hidden="true" className="hidden dark:block" size={20} />
    </button>
  );
}

export default ThemeToggle;
